/**
 * Flattening the structure tree into the rows a table shows.
 *
 * A pure function in its own file so it is testable without rendering, and
 * because a component file that also exports helpers breaks fast refresh.
 */

import type { StructureNode } from "../../api";

export interface TreeRow {
  node: StructureNode;
  depth: number;
  /** Unique within the tree. A place appears under several companies, so its
   * code alone is not — the path from the root is. */
  path: string;
  hasChildren: boolean;
  isOpen: boolean;
}

/** The visible rows: every head, and the children of every open node. */
export function flatten(
  nodes: readonly StructureNode[],
  open: ReadonlySet<string>,
  depth = 0,
  parent = "",
): TreeRow[] {
  const rows: TreeRow[] = [];
  for (const node of nodes) {
    const path = `${parent}${node.level}:${node.code}`;
    const isOpen = open.has(path);
    rows.push({ node, depth, path, hasChildren: node.children.length > 0, isOpen });
    if (isOpen) rows.push(...flatten(node.children, open, depth + 1, `${path}/`));
  }
  return rows;
}
